import { EyeOutlined, MoreOutlined } from '@ant-design/icons';
import { Descriptions, Dropdown, Modal, Spin, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import React, { useMemo, useState } from 'react';
import { IoCalendarOutline, IoCallOutline, IoCheckmarkDoneOutline, IoInformationCircleOutline, IoSwapHorizontalOutline } from 'react-icons/io5';
import { useQuoteFollowupsByQuoteId } from '@/features/dashboard/hooks';

import { Table } from '@/components/table';
import { formatDealerAmount } from '@/utils/currency';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { QUOTE_STATUS_COLORS } from '../constants';
import { IDataverseQuote, QuoteFollowUpStatus, QuoteStatus } from '../types';
import { getAvailableActions } from '../utils/';
import { QuoteDetailDropdown } from './quotes/QuoteDetailDropdown';

interface QuoteTableProps {
  quotes: IDataverseQuote[];
  isLoading: boolean;
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onMarkAsRead?: (quote: IDataverseQuote) => void;
  onView?: (quote: IDataverseQuote) => void;
  onFollowUp?: (quote: IDataverseQuote) => void;
  onSend?: (quote: IDataverseQuote) => void;
  onConvert?: (quote: IDataverseQuote) => void;
  onStatusChange?: (quote: IDataverseQuote, status: QuoteStatus) => void;
  onMarkLost?: (quote: IDataverseQuote, reason: string) => void;
  onAddNote?: (quote: IDataverseQuote, note: string) => void;
  onFollowUpClick?: (quote: IDataverseQuote) => void;
  onProjectClick?: (quote: IDataverseQuote) => void;
  onConvertedClick?: (quote: IDataverseQuote) => void;
  onExpiredClick?: (quote: IDataverseQuote) => void;
  onCompleteFollowUp?: (quote: IDataverseQuote) => void;
  canConvertQuoteToOrder?: boolean;
  canManageQuotesStatus?: boolean;
  canViewFollowup?: boolean;
  canManageFollowup?: boolean;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  } catch {
    return 'N/A';
  }
};

export const QuoteTable: React.FC<QuoteTableProps> = ({
  quotes,
  isLoading,
  current,
  pageSize,
  total,
  nextLink,
  totalRecordCountLimitExceeded,
  onPageChange,
  onMarkAsRead: _onMarkAsRead,
  onView,
  onFollowUp: _onFollowUp,
  onSend,
  onStatusChange,
  onMarkLost,
  onAddNote,
  onFollowUpClick,
  onConvertedClick,
  onExpiredClick,
  onCompleteFollowUp,
  canConvertQuoteToOrder = true,
  canManageQuotesStatus = true,
  canViewFollowup = true,
  canManageFollowup = true,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [followupModalQuoteId, setFollowupModalQuoteId] = useState<string | null>(null);

  const { data: followupsData, isLoading: isFollowupsLoading } = useQuoteFollowupsByQuoteId(followupModalQuoteId ?? '');

  const toggleExpand = (record: IDataverseQuote) => {
    setExpandedRowKeys((prev) =>
      prev.includes(record.quoteid) ? prev.filter((k) => k !== record.quoteid) : [record.quoteid]
    );
  };

  const columns = useMemo(() => {
    return [
      {
        title: 'Quote Number',
        dataIndex: 'quotenumber',
        className: 'font-semibold',
        render: (quotenumber: string) => quotenumber || 'N/A',
      },
      {
        title: 'Project Reference',
        dataIndex: "dogma_projectreference",
      },
      {
        title: 'Project Type',
        dataIndex: "dogma_projecttype@OData.Community.Display.V1.FormattedValue"
      },
      {
        title: 'Customer/Company',
        dataIndex: "_customerid_value@OData.Community.Display.V1.FormattedValue",
        render: (customerName: string | undefined, record: IDataverseQuote) => {
          return <div className='flex flex-col'>
            <span>{customerName}</span>
            <span className='text-text-secondary'>{record["_dogma_customer_value@OData.Community.Display.V1.FormattedValue"]}</span>
          </div>
        }

      },
      {
        title: 'Date',
        dataIndex: "createdon",
        // Always render via our own en-GB formatter — Dataverse's own
        // @OData...FormattedValue is formatted per the org/user's locale
        // (e.g. en-US, MM/DD/YYYY), not fixed to UK format.
        render: (_: string | undefined, record: IDataverseQuote) =>
          formatDate(record.createdon),
      },
      {
        title: 'Amount',
        dataIndex: 'totalamount',
        align: 'right' as const,
        className: 'font-medium',
        render: (_: number | undefined, record: IDataverseQuote) => {
          const amount = record.dogma_vsstotalamount ?? 0;
          const dealersCurrency = record?.dogma_DealersCurrency;
          const exchangeRate = record.dogma_exchangerate ?? 1;
          return (
            <div className="flex flex-col font-semibold items-end">
              <span>{formatDealerAmount(amount, dealersCurrency, exchangeRate)}</span>
            </div>
          );
        },
      },
      {
        title: 'Windows',
        key: 'windows',
        render: (_: unknown, record: IDataverseQuote) => {
          const details = record.quoteDetails;
          if (!details?.length) return <span className="text-gray-400">—</span>;
          const totalQuantity = details.reduce((sum, d) => sum + (d.quantity ?? 0), 0);
          return (
            <div className="flex flex-wrap gap-1">
              {totalQuantity}
            </div>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: "statuscode@OData.Community.Display.V1.FormattedValue",
        render: (statusLabel: string | undefined, record: IDataverseQuote) => (
          <Tag
            color={QUOTE_STATUS_COLORS[record.statuscode] || 'default'}
            style={{
              borderRadius: '12px',
              padding: '4px 12px',
              border: 'none',
            }}
          >
            {statusLabel}
          </Tag>
        ),
      },
      {
        title: 'Follow Up',
        dataIndex: 'dogma_followup',
        render: (followUp: number | undefined, record: IDataverseQuote) => {
          if (!followUp || followUp === QuoteFollowUpStatus.NONE) return <span className="text-gray-400">—</span>;
          const isInProgress = followUp === QuoteFollowUpStatus.IN_PROGRESS;
          const color = isInProgress ? 'orange' : 'green';
          const label = isInProgress ? 'In Progress' : 'Resolved';
          return (
            <Tag
              color={color}
              style={{ borderRadius: '12px', padding: '4px 10px', border: 'none', cursor: isInProgress ? 'pointer' : 'default' }}
              onClick={isInProgress ? (e) => { e.stopPropagation(); setFollowupModalQuoteId(record.quoteid); } : undefined}
            >
              <span className="flex items-center gap-1">
                {label}
                {isInProgress && <IoInformationCircleOutline size={13} style={{ verticalAlign: 'middle' }} />}
              </span>
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, record: IDataverseQuote) => {
          const availableActions = getAvailableActions(record.statuscode);

          const allMenuItems: any = [
            {
              key: 'view',
              icon: <EyeOutlined />,
              label: 'View Quote',
              onClick: () => onView?.(record),
            },
            // If follow-up is in progress show Complete; otherwise show Follow Up
            record.dogma_followup === QuoteFollowUpStatus.IN_PROGRESS && canManageFollowup && {
              key: 'complete-followup',
              icon: <IoCheckmarkDoneOutline className="text-base" />,
              label: 'Mark Follow-up Complete',
              onClick: () => onCompleteFollowUp?.(record),
            },
            availableActions.canMarkAsFollowUp && record.dogma_followup !== QuoteFollowUpStatus.IN_PROGRESS && canManageQuotesStatus && {
              key: 'followup',
              icon: <IoCallOutline className="text-base" />,
              label: 'Follow Up',
              onClick: () => onFollowUpClick?.(record),
            },
            {
              type: 'divider' as const,
              key: 'divider-1',
            },
            // Status change actions - require canManageQuotesStatus permission
            // availableActions.canMarkAsRevised && canManageQuotesStatus && {
            //   key: 'project',
            //   icon: <IoBriefcaseOutline className="text-base" />,
            //   label: 'Mark as Revised',
            //   onClick: () => onProjectClick?.(record),
            // },
            // Convert to order - require canConvertQuoteToOrder permission
            // availableActions.canConvertToOrder && canConvertQuoteToOrder && {
            //   key: 'converted',
            //   icon: <IoSwapHorizontalOutline className="text-base" />,
            //   label: 'Place Order',
            //   onClick: () => onConvertedClick?.(record),
            // },
            // availableActions.canMarkAsExpired && canManageQuotesStatus && {
            //   key: 'expired',
            //   icon: <IoCalendarOutline className="text-base" />,
            //   label: 'Mark as Expired',
            //   onClick: () => onExpiredClick?.(record),
            // },
          ]
            .filter(Boolean)
            .filter((item: any, index, array: any) => {
              // Remove divider if it's the first item
              if (index === 0 && item.type === 'divider') return false;
              // Remove divider if it's the last item
              if (index === array.length - 1 && item.type === 'divider') return false;
              // Remove divider if the next item is also a divider
              if (item.type === 'divider' && array[index + 1]?.type === 'divider') return false;
              return true;
            });

          return (
            <Dropdown
              rootClassName="row_actions_dropdown"
              menu={{
                items: allMenuItems,
              }}
            >
              <MoreOutlined />
            </Dropdown>
          );
        },
      },
    ] as ColumnsType<IDataverseQuote>;
  }, [onView, onFollowUpClick, onConvertedClick, onExpiredClick, onCompleteFollowUp, canConvertQuoteToOrder, canManageQuotesStatus, canManageFollowup]);

  const followups = followupsData?.value ?? [];

  return (
    <>
      {loadingTableHandler(
        isLoading,
        <Table<IDataverseQuote>
          isLoading={isLoading}
          columns={columns}
          dataSource={quotes}
          rowKey="quoteid"
          expandable={{
            expandedRowKeys,
            expandRowByClick: false,
            showExpandColumn: true,
            onExpand: (_expanded, record) => toggleExpand(record),
            expandedRowRender: (record) => {
              return (
                <div style={{ width: 0, minWidth: '100%', overflow: 'hidden' }}>
                  <QuoteDetailDropdown
                    quoteId={record.quoteid}
                    isExpanded={true}
                    onToggle={() => toggleExpand(record)}
                    onStatusChange={() => {}}
                    canConvertQuoteToOrder={canConvertQuoteToOrder}
                    canManageQuotesStatus={canManageQuotesStatus}
                    canManageFollowup={canManageFollowup}
                    onFollowUp={() => onFollowUpClick?.(record)}
                    onCompleteFollowUp={() => onCompleteFollowUp?.(record)}
                    onConvertToOrder={() => onConvertedClick?.(record)}
                    onStatusSelect={(status) => {
                      if (status === QuoteStatus.FOLLOW_UP_NEEDED) onFollowUpClick?.(record);
                      else if (status === QuoteStatus.CONVERTED) onConvertedClick?.(record);
                      else if (status === QuoteStatus.EXPIRED) onExpiredClick?.(record);
                    }}
                    onAddNote={(note) => onAddNote?.(record, note)}
                  />
                </div>
              );
            },
          }}
          current={current}
          pageSize={pageSize}
          total={total}
          nextLink={nextLink}
          totalRecordCountLimitExceeded={totalRecordCountLimitExceeded}
          handleChange={onPageChange}
        />
      )}

      <Modal
        title="Follow-up Details"
        open={!!followupModalQuoteId}
        onCancel={() => setFollowupModalQuoteId(null)}
        footer={null}
        width={600}
      >
        {isFollowupsLoading ? (
          <div className="flex justify-center py-8"><Spin /></div>
        ) : followups.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No active follow-ups found for this quote.</p>
        ) : (
          <div className="space-y-4">
            {followups.map((fu, idx) => (
              <Descriptions
                key={fu.dogma_quotefollowupid}
                title={followups.length > 1 ? `Follow-up ${idx + 1}` : undefined}
                bordered
                size="small"
                column={1}
              >
                <Descriptions.Item label="Name">{fu.dogma_name}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  {fu['dogma_type@OData.Community.Display.V1.FormattedValue'] ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Follow-up Date">
                  {fu['dogma_followupdate@OData.Community.Display.V1.FormattedValue'] ?? fu.dogma_followupdate ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">{fu.dogma_notes || '—'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  {fu['statuscode@OData.Community.Display.V1.FormattedValue'] ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {fu['createdon@OData.Community.Display.V1.FormattedValue'] ?? fu.createdon}
                </Descriptions.Item>
              </Descriptions>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

export default QuoteTable;

